import { Check, Copy, FilePlus2, FolderOpen, Hexagon, Pencil, Square, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  deleteMap as deleteMapRecord,
  duplicateMap,
  renameMap,
} from "@/src/core/persistence/repository";
import type { MapSummary } from "@/src/core/persistence/schema";
import {
  createAndOpenBlankMap,
  openMapById,
  refreshLibrary,
} from "@/src/core/persistence/session";
import { useT } from "@/src/i18n";
import { useMapStore } from "@/src/store/useMapStore";

interface MapLibraryProps {
  onClose: () => void;
  onMessage: (message: string) => void;
}

const iconButton =
  "grid h-8 w-8 place-items-center rounded-lg text-stone-400 transition hover:bg-white/10 hover:text-white";

const useFormatWhen = () => {
  const t = useT();
  return (timestamp: number) => {
    const minutes = Math.round((Date.now() - timestamp) / 60000);
    if (minutes < 1) return t("library.justNow");
    if (minutes < 60) return t("library.minutesAgo", { count: minutes });
    const hours = Math.round(minutes / 60);
    if (hours < 24) return t("library.hoursAgo", { count: hours });
    return new Date(timestamp).toLocaleDateString();
  };
};

export function MapLibrary({ onClose, onMessage }: MapLibraryProps) {
  const currentMapId = useMapStore((state) => state.currentMapId);
  const currentTitle = useMapStore((state) => state.meta.title);
  const [maps, setMaps] = useState<MapSummary[]>([]);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [busy, setBusy] = useState(false);
  const t = useT();
  const formatWhen = useFormatWhen();

  const reload = useCallback(async () => {
    try {
      setMaps(await refreshLibrary());
    } catch {
      onMessage(t("library.readFailed"));
    }
  }, [onMessage, t]);

  // Reads the library out of IndexedDB. currentTitle is a dependency so that
  // renaming from the header input refreshes this list too. The cancelled flag
  // keeps a slow read from setting state after the panel has closed.
  useEffect(() => {
    let cancelled = false;
    refreshLibrary()
      .then((summaries) => { if (!cancelled) setMaps(summaries); })
      .catch(() => { if (!cancelled) onMessage(t("library.readFailed")); });
    return () => { cancelled = true; };
  }, [currentMapId, currentTitle, onMessage, t]);

  const guard = async (action: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    try {
      await action();
    } catch {
      onMessage(t("library.actionFailed"));
    } finally {
      setBusy(false);
    }
  };

  const handleOpen = (id: string) => guard(async () => {
    if (id === currentMapId) return;
    const opened = await openMapById(id);
    onMessage(opened ? t("library.opened") : t("library.openFailed"));
    await reload();
  });

  const handleNew = () => guard(async () => {
    const record = await createAndOpenBlankMap();
    onMessage(t("library.created", { title: record.document.meta.title }));
    await reload();
  });

  const handleDuplicate = (id: string) => guard(async () => {
    const copy = await duplicateMap(id);
    onMessage(copy ? t("library.duplicated", { title: copy.document.meta.title }) : t("library.duplicateFailed"));
    await reload();
  });

  const handleDelete = (summary: MapSummary) => guard(async () => {
    if (!window.confirm(t("library.deleteConfirm", { title: summary.title }))) return;
    await deleteMapRecord(summary.id);
    if (summary.id === currentMapId) {
      const remaining = await refreshLibrary();
      if (remaining[0]) await openMapById(remaining[0].id);
      else await createAndOpenBlankMap();
    }
    onMessage(t("library.deleted", { title: summary.title }));
    await reload();
  });

  const commitRename = (id: string) => guard(async () => {
    const name = draftName.trim();
    setRenamingId(null);
    if (!name) return;
    if (id === currentMapId) {
      // Route through the store so the header input and autosave stay in step.
      useMapStore.getState().setTitle(name);
    } else {
      await renameMap(id, name);
    }
    await reload();
  });

  return (
    <aside className="flex max-h-full w-[min(340px,calc(100vw-24px))] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#1c1b18]/97 shadow-2xl shadow-black/30 backdrop-blur">
      <div className="flex shrink-0 items-start justify-between border-b border-white/8 px-4 py-3">
        <div>
          <p className="flex items-center gap-2 text-xs font-semibold text-stone-100">
            <FolderOpen size={14} className="text-[#d55b51]" /> {t("library.title")}
          </p>
          <p className="mt-1 text-[10px] text-stone-500">
            {t("library.count", { count: maps.length })}
          </p>
        </div>
        <button type="button" onClick={onClose} className="rounded-md p-1 text-stone-500 hover:bg-white/5 hover:text-white" aria-label={t("library.close")}>
          <X size={15} />
        </button>
      </div>

      <div className="shrink-0 p-3">
        <button
          type="button"
          onClick={handleNew}
          disabled={busy}
          className="flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[#ad342e] text-[11px] font-semibold text-white transition hover:bg-[#bf3d35] disabled:opacity-50"
        >
          <FilePlus2 size={14} /> {t("library.newMap")}
        </button>
      </div>

      <ul className="min-h-0 flex-1 space-y-1.5 overflow-y-auto px-3 pb-3">
        {maps.length === 0 && (
          <li className="rounded-xl border border-white/6 bg-white/[0.02] px-3 py-4 text-center text-[10px] text-stone-500">
            {t("library.empty")}
          </li>
        )}
        {maps.map((summary) => {
          const active = summary.id === currentMapId;
          const isHex = summary.gridType === "hex";
          return (
            <li
              key={summary.id}
              className={`rounded-xl border px-3 py-2.5 transition ${active ? "border-[#b4362f]/50 bg-[#a8322c]/15" : "border-white/6 bg-white/[0.025] hover:border-white/15"}`}
            >
              {renamingId === summary.id ? (
                <div className="flex items-center gap-1.5">
                  <input
                    value={draftName}
                    onChange={(event) => setDraftName(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") void commitRename(summary.id);
                      if (event.key === "Escape") setRenamingId(null);
                    }}
                    maxLength={80}
                    autoFocus
                    className="h-8 min-w-0 flex-1 rounded-lg border border-white/10 bg-[#2a2722] px-2 text-[11px] text-stone-100 outline-none focus:border-[#c34a40]"
                    aria-label={t("library.newName")}
                  />
                  <button type="button" onClick={() => void commitRename(summary.id)} className={iconButton} aria-label={t("library.saveName")}>
                    <Check size={14} />
                  </button>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => void handleOpen(summary.id)}
                    className="block w-full text-start"
                    aria-current={active}
                  >
                    <span className="flex items-center gap-1.5 text-[11px] font-semibold text-stone-100">
                      {isHex ? <Hexagon size={11} className="shrink-0 text-[#e27b71]" /> : <Square size={11} className="shrink-0 text-[#e27b71]" />}
                      <span className="truncate">{summary.title}</span>
                      {active && <span className="ms-auto shrink-0 rounded bg-[#a8322c]/40 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-[#f0a49c]">{t("library.open")}</span>}
                    </span>
                    <span className="mt-1 block text-[9px] text-stone-500">
                      {isHex ? t("status.hexes", { count: summary.hexCount }) : t("status.regions", { count: summary.regionCount })}
                      {" · "}{t("status.objects", { count: summary.objectCount })}
                      {" · "}{formatWhen(summary.updatedAt)}
                    </span>
                  </button>
                  <div className="mt-1.5 flex items-center gap-0.5 border-t border-white/6 pt-1.5">
                    <button
                      type="button"
                      onClick={() => { setRenamingId(summary.id); setDraftName(summary.title); }}
                      className={iconButton}
                      aria-label={t("library.rename", { title: summary.title })}
                    >
                      <Pencil size={13} />
                    </button>
                    <button type="button" onClick={() => void handleDuplicate(summary.id)} className={iconButton} aria-label={t("library.duplicate", { title: summary.title })}>
                      <Copy size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDelete(summary)}
                      className={`${iconButton} hover:bg-red-500/15 hover:text-red-300`}
                      aria-label={t("library.delete", { title: summary.title })}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </>
              )}
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
